import { collection, getDocs } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { auth, db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

const styles = {
  screen: { minHeight: '100vh', backgroundColor: '#F4F6F9', fontFamily: 'system-ui, sans-serif' },
  main: { marginLeft: 220, padding: 24 },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
  pageTitle: { fontSize: 22, fontWeight: '700', color: '#0A2D5A' },
  pageSub: { fontSize: 13, color: '#888', marginTop: 2 },
  card: { backgroundColor: 'white', borderRadius: 12, border: '0.5px solid #e0e0e0', overflow: 'hidden' },
  headerRow: { display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #eee', gap: 12, fontSize: 11, fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: 1 },
  row: { display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '0.5px solid #f0f0f0', gap: 12 },
  colName: { flex: 2, minWidth: 0 },
  colEmail: { flex: 2, minWidth: 0, fontSize: 13, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  colStatus: { flex: 1 },
  colDate: { flex: 1, fontSize: 13, color: '#555' },
  agentName: { fontSize: 15, fontWeight: '600', color: '#111' },
  statusBadge: { fontSize: 11, padding: '3px 10px', borderRadius: 10, fontWeight: '600', whiteSpace: 'nowrap', display: 'inline-block' },
  denied: { padding: 40, textAlign: 'center', color: '#CC0000', fontSize: 16, fontWeight: '600' },
  loading: { padding: 40, textAlign: 'center', color: '#888', fontSize: 15 },
  colAction: { flex: 1, textAlign: 'right' },
  resetBtn: { padding: '6px 12px', borderRadius: 6, border: '1px solid #1D6FA4', fontSize: 12, cursor: 'pointer', backgroundColor: 'white', color: '#1D6FA4', fontWeight: '600' },
  toast: { position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)', backgroundColor: '#0A2D5A', color: 'white', padding: '12px 24px', borderRadius: 10, fontSize: 14, fontWeight: '600', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', zIndex: 2000 },
};

const STATUS_STYLE = {
  active:    { bg: '#DCFCE7', color: '#166534' },
  past_due:  { bg: '#FEE2E2', color: '#CC0000' },
  canceling: { bg: '#FEF3C7', color: '#92400E' },
  expired:   { bg: '#F3F4F6', color: '#6B7280' },
  comped:    { bg: '#DBEAFE', color: '#1E40AF' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || { bg: '#F4F6F9', color: '#888' };
  return (
    <span style={{ ...styles.statusBadge, backgroundColor: s.bg, color: s.color }}>
      {status || 'unknown'}
    </span>
  );
}

function formatDate(value) {
  if (!value) return '\u2014';
  try {
    const d = value.toDate ? value.toDate() : new Date(value);
    if (isNaN(d.getTime())) return '\u2014';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '\u2014';
  }
}

export default function Admin() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(undefined);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const check = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/login');
        return;
      }
      const tokenResult = await user.getIdTokenResult();
      if (tokenResult.claims.admin === true) {
        setIsAdmin(true);
        fetchAgents();
      } else {
        setIsAdmin(false);
      }
    };
    check();
  }, [navigate]);

  const fetchAgents = async () => {
    try {
      const snap = await getDocs(collection(db, 'agents'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const av = a.purchaseDate?.toDate ? a.purchaseDate.toDate().getTime() : 0;
        const bv = b.purchaseDate?.toDate ? b.purchaseDate.toDate().getTime() : 0;
        return bv - av;
      });
      setAgents(list);
    } catch (err) {
      console.error('Error loading agents:', err);
    }
    setLoading(false);
  };

  const handleReset = async (email) => {
    try {
      const fns = getFunctions();
      const call = httpsCallable(fns, 'adminSendPasswordReset');
      await call({ email });
      setToast('Reset email sent to ' + email);
    } catch (err) {
      console.error('Reset failed:', err);
      setToast('Failed to send reset email.');
    }
    setTimeout(() => setToast(null), 3000);
  };

  if (isAdmin === undefined) {
    return (
      <div style={styles.screen}>
        <Sidebar admin />
        <div style={styles.main}><div style={styles.loading}>Checking access...</div></div>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div style={styles.screen}>
        <Sidebar admin />
        <div style={styles.main}><div style={styles.denied}>Access denied. This page is for administrators only.</div></div>
      </div>
    );
  }

  return (
    <div style={styles.screen}>
      <Sidebar admin />
      <div style={styles.main}>

        {toast && <div style={styles.toast}>{toast}</div>}

        <div style={styles.topbar}>
          <div>
            <div style={styles.pageTitle}>Admin — Agents</div>
            <div style={styles.pageSub}>
              {loading ? 'Loading...' : `${agents.length} agent${agents.length === 1 ? '' : 's'}`}
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.headerRow}>
            <div style={styles.colName}>Name</div>
            <div style={styles.colEmail}>Email</div>
            <div style={styles.colStatus}>Status</div>
            <div style={styles.colDate}>Purchased</div>
            <div style={styles.colDate}>Renews</div>
            <div style={styles.colAction}>Actions</div>
          </div>

          {loading && <div style={styles.loading}>Loading agents...</div>}

          {!loading && agents.length === 0 && (
            <div style={styles.loading}>No agents yet.</div>
          )}

          {!loading && agents.map(agent => (
            <div key={agent.id} style={styles.row}>
              <div style={styles.colName}>
                <div style={styles.agentName}>{agent.name || '(no name)'}</div>
              </div>
              <div style={styles.colEmail}>{agent.email || '\u2014'}</div>
              <div style={styles.colStatus}><StatusBadge status={agent.accountStatus} /></div>
              <div style={styles.colDate}>{formatDate(agent.purchaseDate)}</div>
              <div style={styles.colDate}>{formatDate(agent.nextRenewalDate)}</div>
              <div style={styles.colAction}>
                <button style={styles.resetBtn} onClick={() => handleReset(agent.email)}>Reset Password</button>
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}








